// Artisans Favoris
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, MapPin, Phone, MessageCircle, Bookmark, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEMO_FAVORIS = [
  {
    id: 'fav-1',
    name: 'Plomberie Durand & Fils',
    specialty: 'Plombier',
    rating: 4.9,
    reviews: 128,
    city: 'Paris 15e',
    verified: true,
    badges: ['RGE certifié', 'Réactif', 'Pro'],
  },
  {
    id: 'fav-2',
    name: 'Léonard Martin Menuiserie',
    specialty: 'Menuisier',
    rating: 4.7,
    reviews: 85,
    city: 'Versailles',
    verified: true,
    badges: ['Artisan du Patrimoine', 'Ponctuel'],
  },
  {
    id: 'fav-3',
    name: 'Électricité Pro Martin',
    specialty: 'Électricien',
    rating: 4.8,
    reviews: 203,
    city: 'Boulogne-Billancourt',
    verified: false,
    badges: ['IRVE', 'Domotique', 'Réactif'],
  },
];

function ArtisanFavoriCard({ artisan, onRemove, onContact }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:border-orange-200 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
          {artisan.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900">{artisan.name}</h3>
            {artisan.verified && (
              <CheckCircle size={16} className="text-blue-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-orange-600 font-medium">{artisan.specialty}</p>

          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Star size={14} className="text-amber-500 fill-amber-500" />
              <span className="font-medium text-gray-900">{artisan.rating}</span>
              <span className="text-xs">({artisan.reviews} avis)</span>
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {artisan.city}
            </span>
          </div>

          {artisan.badges?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {artisan.badges.map((badge, i) => (
                <span key={i} className="px-2 py-0.5 bg-[#1E3A5F]/5 text-[#1E3A5F] text-xs font-medium rounded-full border border-[#1E3A5F]/10">
                  {badge}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => onRemove(artisan.id)}
          className="p-2 hover:bg-red-50 text-gray-300 hover:text-red-500 rounded-lg transition-colors flex-shrink-0"
        >
          <Trash2 size={18} />
        </button>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        <button
          onClick={() => onContact(artisan, 'phone')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-white hover:border-orange-200 hover:text-orange-600 transition-all"
        >
          <Phone size={14} /> Appeler
        </button>
        <button
          onClick={() => onContact(artisan, 'devis')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-all shadow-sm shadow-orange-500/25"
        >
          <MessageCircle size={14} /> Demander un devis
        </button>
      </div>
    </motion.div>
  );
}

export default function ArtisansFavoris() {
  const navigate = useNavigate();
  const [favoris, setFavoris] = useState(DEMO_FAVORIS);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(true);

  useEffect(() => {
    fetchFavoris();
  }, []);

  async function fetchFavoris() {
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || '';
      const res = await fetch(`${API_URL}/api/ecosystem/profile/me/followed-artisans?per_page=50`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.followed_artisans || []).map(f => ({
          id: f.artisan?.seller_id || f.artisan_id,
          name: f.artisan?.company_name || f.artisan?.metier || 'Artisan',
          specialty: f.artisan?.metier || '',
          rating: f.artisan?.rating || null,
          reviews: f.artisan?.prestations_count || 0,
          city: f.artisan?.city || '',
          verified: false,
          badges: [],
        }));
        if (mapped.length > 0) {
          setFavoris(mapped);
          setIsDemo(false);
        }
      }
    } catch (e) {
      console.error('fetchFavoris error', e);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(artisanId) {
    setFavoris(prev => prev.filter(f => f.id !== artisanId));
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || '';
      await fetch(`${API_URL}/api/ecosystem/artisans/${artisanId}/follow`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('unfollow error', e);
    }
  }

  function handleContact(artisan, type) {
    if (type === 'phone') {
      toast.info(`Coordonnées de ${artisan.name} bientôt disponibles`);
    } else {
      navigate(`/decrire-projet`);
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Artisans Favoris</h1>
          <p className="text-sm text-gray-500">
            {loading ? '…' : `${favoris.length} artisan${favoris.length > 1 ? 's' : ''} sauvegardé${favoris.length > 1 ? 's' : ''}`}
          </p>
        </div>
        {isDemo && (
          <span className="px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
            Aperçu démo
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-orange-500" />
        </div>
      ) : favoris.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun favori</h3>
          <p className="text-gray-500 text-sm">Sauvegarde des artisans pour les retrouver facilement</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {favoris.map(artisan => (
            <ArtisanFavoriCard
              key={artisan.id}
              artisan={artisan}
              onRemove={handleRemove}
              onContact={handleContact}
            />
          ))}
        </div>
      )}
    </div>
  );
}
